import { AudioContext as ThreeAudioContext } from 'three'
import { subscribeKey } from 'valtio/utils'
import { options } from './optionsStorage'
import { isCypress } from './standaloneUtils'
import { reportWarningOnce } from './utils'

let audioContext: AudioContext
const sounds: Record<string, any> = {}

// Track currently playing sounds and their gain nodes
const activeSounds: Array<{
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  volumeMultiplier: number;
  isMusic: boolean;
}> = []
window.activeSounds = activeSounds

/** Invoke resume directly in the user gesture, before any fetch or XR promise is awaited. */
export function resumeGameAudio(): Promise<void> {
  const resumes: Promise<unknown>[] = []
  try {
    audioContext ??= new window.AudioContext()
    const contexts = new Set<AudioContext>([audioContext, ThreeAudioContext.getContext() as unknown as AudioContext])
    for (const context of contexts) if (context.state !== 'running' && context.state !== 'closed') resumes.push(context.resume())
  } catch (error) {
    reportWarningOnce('audioContext', 'Failed to activate audio. Some sounds will not play')
  }
  return Promise.allSettled(resumes).then(() => {})
}
window.addEventListener('pointerdown', () => { void resumeGameAudio() }, { capture: true, passive: true })
window.addEventListener('keydown', () => { void resumeGameAudio() }, { capture: true, passive: true })
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void resumeGameAudio() })

const loadingSounds = new Map<string, Promise<boolean>>()

export function loadSound(path: string, contents = path): Promise<boolean> {
  if (sounds[path]) return Promise.resolve(true)
  const existing = loadingSounds.get(path)
  if (existing) return existing
  const loading = (async () => {
    try {
      audioContext ??= new window.AudioContext()
      const response = await window.fetch(contents)
      if (!response.ok) throw new Error(`Failed to load sound ${path}`)
      const bytes = await response.arrayBuffer()
      sounds[path] = await audioContext.decodeAudioData(bytes)
      return true
    } catch (error) {
      console.warn(`Failed to load sound ${path}:`, error)
      if (isCypress()) throw error
      return false
    }
  })().finally(() => { loadingSounds.delete(path) })
  loadingSounds.set(path, loading)
  return loading
}
window.loadSound = loadSound

export const loadOrPlaySound = async (url, soundVolume = 1, loadTimeout = options.remoteSoundsLoadTimeout, loop = false, isMusic = false, pitch = 1) => {
  const soundBuffer = sounds[url]
  if (!soundBuffer) {
    const start = Date.now()
    const loaded = await loadSound(url)
    if (!loaded || Date.now() - start > loadTimeout) return
  }

  return playSound(url, soundVolume, loop, isMusic, pitch)
}

async function playSound (url, soundVolume = 1, loop = false, isMusic = false, pitch = 1) {
  const volume = soundVolume * (options.volume / 100) * (isMusic ? options.musicVolume / 100 : 1)

  if (!volume) return

  try {
    audioContext ??= new window.AudioContext()
  } catch (err) {
    reportWarningOnce('audioContext', 'Failed to create audio context. Some sounds will not play')
    return
  }

  const soundBuffer = sounds[url]
  if (!soundBuffer) {
    console.warn(`Sound ${url} not loaded yet`)
    return
  }

  const gainNode = audioContext.createGain()
  const source = audioContext.createBufferSource()
  source.buffer = soundBuffer
  source.loop = loop
  source.playbackRate.value = Number.isFinite(pitch) && pitch > 0 ? pitch : 1
  source.connect(gainNode)
  gainNode.connect(audioContext.destination)
  gainNode.gain.value = volume
  source.start(0)

  // Add to active sounds
  activeSounds.push({ source, gainNode, volumeMultiplier: soundVolume, isMusic })

  const callbacks = [] as Array<() => void>
  source.onended = () => {
    // Remove from active sounds when finished
    const index = activeSounds.findIndex(s => s.source === source)
    if (index !== -1) activeSounds.splice(index, 1)

    for (const callback of callbacks) {
      callback()
    }
    callbacks.length = 0
    source.disconnect(); gainNode.disconnect()
  }

  return {
    onEnded (callback: () => void) {
      callbacks.push(callback)
    },
    stop () {
      try {
        source.stop()
        // Remove from active sounds
        const index = activeSounds.findIndex(s => s.source === source)
        if (index !== -1) activeSounds.splice(index, 1)
      } catch (err) {
        console.warn('Failed to stop sound:', err)
      }
    },
    gainNode,
  }
}
window.playSound = playSound

export function stopAllSounds () {
  for (const { source } of activeSounds) {
    try {
      source.stop()
    } catch (err) {
      console.warn('Failed to stop sound:', err)
    }
  }
  activeSounds.length = 0
}

export function stopSound (url: string) {
  const soundIndex = activeSounds.findIndex(s => s.source.buffer === sounds[url])
  if (soundIndex !== -1) {
    const { source } = activeSounds[soundIndex]
    try {
      source.stop()
    } catch (err) {
      console.warn('Failed to stop sound:', err)
    }
    activeSounds.splice(soundIndex, 1)
  }
}

export function changeVolumeOfCurrentlyPlayingSounds (newVolume: number, newMusicVolume: number) {
  const normalizedVolume = newVolume / 100
  for (const { gainNode, volumeMultiplier, isMusic } of activeSounds) {
    try {
      gainNode.gain.value = normalizedVolume * volumeMultiplier * (isMusic ? newMusicVolume / 100 : 1)
    } catch (err) {
      console.warn('Failed to change sound volume:', err)
    }
  }
}

subscribeKey(options, 'volume', () => {
  changeVolumeOfCurrentlyPlayingSounds(options.volume, options.musicVolume)
})

subscribeKey(options, 'musicVolume', () => {
  changeVolumeOfCurrentlyPlayingSounds(options.volume, options.musicVolume)
})
